import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "settingsEmail" }

import { ref, computed, inject, onMounted } from 'vue';
import { CompassEnums } from '@sellsy/compass2';
import type { Translator, PushToast } from 'js/shims-vue';
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import { getEmailSettings } from 'api/emailEngine/emailEngineService';
import EmailSettingAccountSynced from 'vcomponents/email/EmailSettingAccountSynced.vue';
import EmailSettingAccountNotSynced from 'vcomponents/email/EmailSettingAccountNotSynced.vue';
import DisconnectPopup from 'vcomponents/email/DisconnectPopup.vue';
import EmailSettingPreferences from 'vcomponents/email/emailSettings/EmailSettingPreferences.vue';

declare const SellsyJS: { WebServiceCaller: { post: Function } };

interface AccountData {
    isSetup: 'Y' | 'N';
    email?: string;
    modeName?: string;
    status?: string;
    isNylas?: 'Y' | 'N';
}

interface EmailPrefsData {
    emailsSentFromCorp: 'Y' | 'N';
    onboarding_setup?: 'Y' | 'N';
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SettingsEmail',
  setup(__props) {

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const account = ref<AccountData>({ isSetup: 'N' });
const emailPrefs = ref<EmailPrefsData>({ emailsSentFromCorp: 'N' });
const isLoading = ref(true);
const emailSuccessfullyLinked = ref(false);
const showDisconnectPopup = ref(false);
const privateEmail = ref(false);
const automaticallyAttachDocumentValue = ref(true);
const ccEmails = ref<string[]>([]);

const isSetup = computed(() => account.value.isSetup === 'Y');
const isEmailsSentFromCorp = computed(() => emailPrefs.value.emailsSentFromCorp === 'Y');
const isSyncMode = computed(() => account.value.isNylas === 'Y');

const getAccountData = (connection: Record<string, string> | null): AccountData => {
    if (connection === null) {
        return { isSetup: 'N' };
    }
    return {
        isSetup: 'Y',
        email: connection.email,
        modeName: connection.type === 'synchronization'
            ? $t.__('email.SettingsEmail.syncMode')
            : $t.__('email.SettingsEmail.transferMode'),
        status: connection.status,
        isNylas: connection.type === 'synchronization' ? 'Y' : 'N',
    };
};

const getEmailPrefsData = (connection: Record<string, string> | null): EmailPrefsData => {
    if (connection === null) {
        return { emailsSentFromCorp: 'N' };
    }
    return {
        emailsSentFromCorp: connection.emails_sent_from_corp ? 'Y' : 'N',
        onboarding_setup: connection.onboarding_setup ? 'Y' : 'N',
    };
};

const getSettings = () => {
    isLoading.value = true;
    getEmailSettings()
        .then((response: { data: Record<string, unknown> }) => {
            const connection = response.data.connection as Record<string, string> | null;
            account.value = getAccountData(connection);
            emailPrefs.value = getEmailPrefsData(connection);
            isLoading.value = false;
            privateEmail.value = response.data.private_mode as boolean;
            automaticallyAttachDocumentValue.value =
                response.data.automatically_attach_document as boolean;
            ccEmails.value = (response.data.cc_emails as string[]) || [];

            if (emailSuccessfullyLinked.value && connection) {
                $pushToast({
                    type: CompassEnums.ToastType.SUCCESS,
                    close: true,
                    icon: true,
                    text: $t.__('settings.SettingsEmail.email_correctly_configured'),
                });
            }
        })
        .catch((error: string) => {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                text: error,
            });
        });
};

const logout = () => {
    isLoading.value = true;
    SellsyJS.WebServiceCaller.post('?_f=prefsNylas&action=logout', {}, {}, () => {
        getSettings();
        showDisconnectPopup.value = false;
    });
};

onMounted(() => {
    getSettings();
    const params = new URLSearchParams(window.location.search);
    emailSuccessfullyLinked.value = !!params.get('status');
});

return (_ctx: any,_cache: any) => {
  const _component_CompassSkeleton = _resolveComponent("CompassSkeleton")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Card, null, {
      default: _withCtx(() => [
        _createVNode(Heading, {
          labelTitle: _unref($t).__('email.SettingsEmail.title'),
          variant: "h2",
          prefixColoredIcon: { icon: 'mail-opened', color: 'brand' }
        }, null, 8 /* PROPS */, ["labelTitle"]),
        (!isLoading.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (!isSetup.value)
                ? (_openBlock(), _createBlock(EmailSettingAccountNotSynced, _mergeProps({ key: 0 }, account.value, { isEmailsSentFromCorp: isEmailsSentFromCorp.value }), null, 16 /* FULL_PROPS */, ["isEmailsSentFromCorp"]))
                : (isSyncMode.value)
                  ? (_openBlock(), _createBlock(EmailSettingAccountSynced, _mergeProps({ key: 1 }, account.value, {
                      isEmailsSentFromCorp: isEmailsSentFromCorp.value,
                      onUpdateEmailStatus: getSettings,
                      onHandleDisplayDialog: _cache[0] || (_cache[0] = ($event: any) => (showDisconnectPopup.value = true))
                    }), null, 16 /* FULL_PROPS */, ["isEmailsSentFromCorp"]))
                  : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */))
          : (_openBlock(), _createBlock(_component_CompassSkeleton, {
              key: 1,
              class: "settingsEmail__skeleton",
              size: _unref(CompassEnums).SkeletonSize.EXTRA_LARGE
            }, null, 8 /* PROPS */, ["size"])),
        (showDisconnectPopup.value)
          ? (_openBlock(), _createBlock(DisconnectPopup, {
              key: 2,
              visible: showDisconnectPopup.value,
              onClose: _cache[1] || (_cache[1] = ($event: any) => (showDisconnectPopup.value = false)),
              onHandleDisconnect: logout
            }, null, 8 /* PROPS */, ["visible"]))
          : _createCommentVNode("v-if", true)
      ]),
      _: 1 /* STABLE */
    }),
    (isLoading.value || isSetup.value)
      ? (_openBlock(), _createBlock(EmailSettingPreferences, {
          key: 0,
          privateEmail: privateEmail.value,
          automaticallyAttachDocumentValue: automaticallyAttachDocumentValue.value,
          ccEmails: ccEmails.value,
          isLoading: isLoading.value
        }, null, 8 /* PROPS */, ["privateEmail", "automaticallyAttachDocumentValue", "ccEmails", "isLoading"]))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})